import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, User, MessageSquare, Plus, Send, LogOut, AlertTriangle, Search, Calendar, MapPin, FileText, Trash2, X, Sun, Moon, LayoutDashboard, Ticket, Inbox, Users as UsersIcon, Settings as SettingsIcon, Menu, Paperclip, Download, Laptop } from 'lucide-react';
import Dashboard from './components/Dashboard';
import TicketHistory from './components/TicketHistory';
import UserDetail from './components/UserDetail';
import UserLink from './components/UserLink';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Decode a JWT's `exp` claim (seconds) into a millisecond timestamp. Returns null if unreadable.
// This only reads the expiry for client-side UX timing — the server still enforces the real expiry.
const getTokenExpiry = (tok) => {
  try {
    const payload = JSON.parse(atob(tok.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};


// The attachments column is written with JSON.stringify but read back as either a
// parsed array or a raw string depending on the column type, so both shapes are
// live in the wild. The detail view already guards for this; the list needs the
// same guard, and one bad row must not take out the whole list.
const attachmentCount = (value) => {
  try {
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.length : 0;
    }
  } catch {
    return 0;
  }
  return 0;
};

// Badge palettes. The light stops were already here; the dark stops are new.
// Previously the coloured cases were hardcoded light while the neutral cases were
// themed, so on a dark card the colours glared and the neutrals didn't. Every pair
// below clears 4.5:1 against its own background in its own mode.
const getStatusColor = (status) => {
  switch (status) {
    case 'open': return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30';
    case 'in-progress': return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-300 dark:border-yellow-500/30';
    case 'resolved': return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-300 dark:border-green-500/30';
    default: return 'bg-n-50 text-n-700 border-n-200';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'open': return <AlertCircle className="w-4 h-4" />;
    case 'in-progress': return <Clock className="w-4 h-4" />;
    case 'resolved': return <CheckCircle className="w-4 h-4" />;
    default: return <AlertCircle className="w-4 h-4" />;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'urgent': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30';
    case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30';
    case 'low': return 'bg-n-100 text-n-800 border-n-300';
    default: return 'bg-n-100 text-n-800 border-n-300';
  }
};

const relativeDate = (value) => {
  const then = new Date(value);
  if (isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return days + 'd ago';
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// One row definition for every ticket list. The tickets and my-tickets views were
// carrying near-identical copies of a card; they now share this. Module scope, not
// nested inside App, so React keeps the same component identity across renders
// instead of remounting every row on each poll.
// Who gets the dashboard, and where everyone else lands instead.
//
// The dashboard is a work-queue console: aging buckets, load per tech, arrival
// patterns. That's the wrong screen for a teacher, who has three tickets and
// one question -- where's mine. The server already scopes the ticket list for
// role 'staff' down to exactly the tickets they submitted, so the plain ticket
// list IS their my-tickets page, with no extra endpoint and no aggregate
// queries running on their behalf every minute.
//
// Mirrors the isStaff gate further down; both derive from the same role set.
const isTechTeamUser = (u) =>
  !!u && u.role !== 'custodian' && u.role !== 'staff' &&
  (u.role === 'admin' ||
    ['tech', 'tech-admin', 'facilities', 'facilities-admin'].includes(u.role) ||
    ['technology', 'facilities'].includes(u.department));

const homeViewFor = (u) => (isTechTeamUser(u) ? 'dashboard' : 'tickets');

const TicketRow = ({ ticket, onOpen, onComments, showAssignee = true }) => {
  // Prefer the server's attachment_count: it spans both the ticket column and any
  // files on the ticket's comments, which is where they usually are given the
  // email-reply flow. Fall back to parsing the ticket column alone so this still
  // renders if the frontend is deployed ahead of the backend.
  const attachments = ticket.attachment_count !== undefined
    ? Number(ticket.attachment_count) || 0
    : attachmentCount(ticket.attachments);
  const comments = Number(ticket.comment_count) || 0;
  return (
    <div
      onClick={() => onOpen(ticket)}
      className="group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-n-100"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-n-400 tabular-nums flex-shrink-0">#{ticket.id}</span>
          <h3 className="text-sm font-medium text-n-900 truncate">{ticket.title}</h3>
          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium border flex-shrink-0 ${getPriorityColor(ticket.priority)}`}>
            {ticket.priority.toUpperCase()}
          </span>
          {ticket.merged_into && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-medium border flex-shrink-0 bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30">
              Merged into #{ticket.merged_into}
            </span>
          )}
        </div>
        {/* Description, phones only. Pass 4 dropped it from the row to buy back
            scan density on desktop, but on a phone that left no way to tell two
            tickets apart without opening each one. md:hidden restores the context
            on small screens and keeps the desktop row a tight two lines. */}
        {ticket.description && (
          <p
            className="md:hidden mt-1 text-xs text-n-600 overflow-hidden"
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
          >
            {ticket.description}
          </p>
        )}
        <div className="flex items-center gap-1.5 mt-1 text-xs text-n-500 min-w-0">
          <span className="flex-shrink-0">{ticket.department === 'technology' ? 'Tech' : 'Facilities'}</span>
          {ticket.location && <><span aria-hidden="true">·</span><span className="truncate">{ticket.location}</span></>}
          <span aria-hidden="true">·</span>
          <span className="truncate">{ticket.submitted_by_name}</span>
          <span aria-hidden="true">·</span>
          <span className="flex-shrink-0" title={new Date(ticket.created_at).toLocaleString()}>{relativeDate(ticket.created_at)}</span>
          {attachments > 0 && (
            <span className="flex items-center gap-0.5 flex-shrink-0" title={attachments + ' attachment' + (attachments === 1 ? '' : 's')}>
              <span aria-hidden="true">·</span>
              <Paperclip className="w-3 h-3" />
              {attachments}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {comments > 0 && (
          <button
            onClick={(e) => onComments(e, ticket)}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-n-600 hover:bg-n-200 transition"
            title={comments + ' comment' + (comments === 1 ? '' : 's')}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {comments}
          </button>
        )}
        {showAssignee && (
          <span className={`hidden md:inline text-xs truncate max-w-[9rem] ${ticket.assigned_to_name ? 'text-n-600' : 'text-n-400 italic'}`}>
            {ticket.assigned_to_name || 'Unassigned'}
          </span>
        )}
        <span className={`px-2 py-1 rounded text-[11px] font-medium border flex items-center gap-1 whitespace-nowrap ${getStatusColor(ticket.status)}`}>
          {getStatusIcon(ticket.status)}
          {ticket.status.replace('-', ' ').toUpperCase()}
        </span>
      </div>
    </div>
  );
};

const App = () => {
  const SCHOOL_NAME = "Passaic Valley Regional High School";
  const SCHOOL_LOGO = "/pv-logo.png";
  // These now resolve through the CSS variables in index.css rather than holding a
  // literal. All 21 inline style usages follow automatically, and once the Settings
  // page can write --pv-800 at runtime, the whole app restyles with no rebuild.
  const PRIMARY_COLOR = "rgb(var(--pv-800))";
  const PRIMARY_HOVER = "rgb(var(--pv-700))";

  const [token, setToken] = useState(localStorage.getItem('token'));
  // Read the stored theme synchronously on first render so there's no flash of light
  // mode before an effect runs. Falls back to the OS preference for first-time users.
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') return saved;
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  // Restoring a session has to respect the same home rule as a fresh login,
  // or a returning teacher lands on a dashboard the nav won't even show them.
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [view, setView] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? homeViewFor(JSON.parse(stored)) : 'dashboard';
    } catch {
      return 'dashboard';
    }
  });
  const [devices, setDevices] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceTickets, setDeviceTickets] = useState([]);
  const [editingAssigned, setEditingAssigned] = useState(false);
  const [ticketDeviceSearch, setTicketDeviceSearch] = useState('');
  const [showTicketDevicePicker, setShowTicketDevicePicker] = useState(false);
  const [assignedInput, setAssignedInput] = useState('');
  const [savingAssigned, setSavingAssigned] = useState(false);
  // Drives the off-canvas sidebar on small screens only. On md+ the sidebar is
  // always visible and this value is ignored.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
const [newTicket, setNewTicket] = useState({
  title: '',
  department: 'technology',
  priority: 'medium',
  location: '',
  description: '',
  requested_for_user_id: '',
  staffSearchQuery: '',
  showStaffDropdown: false,
  asset_id: '',
  deviceSearchQuery: '',
  showDeviceDropdown: false
});
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allStaffMembers, setAllStaffMembers] = useState([]);
  const [deletedTickets, setDeletedTickets] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionWarning, setSessionWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ticketDraft, setTicketDraft] = useState({});
  
  // State for comment popup on ticket cards
  const [showCommentsForTicket, setShowCommentsForTicket] = useState(null);
  const [cardComments, setCardComments] = useState([]);
  const [cardCommentsLoading, setCardCommentsLoading] = useState(false);
  
  // State for merge functionality
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeableTickets, setMergeableTickets] = useState([]);
  const [selectedMergeTickets, setSelectedMergeTickets] = useState([]);
  const [mergeSearchQuery, setMergeSearchQuery] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  
  // State for user management
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Settings (admin only)
  const [notifySettings, setNotifySettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [extraEmailInput, setExtraEmailInput] = useState('');
  const [recipientPreview, setRecipientPreview] = useState({ technology: [], facilities: [] });
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: '',
    notify_new_ticket_alerts: 'role'
  });
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(null);
  
  // Function to gather device information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  // Detect OS
  let os = 'Unknown OS';
  if (userAgent.indexOf('Win') !== -1) {
    if (userAgent.indexOf('Windows NT 10.0') !== -1) os = 'Windows 10/11';
    else if (userAgent.indexOf('Windows NT 6.3') !== -1) os = 'Windows 8.1';
    else if (userAgent.indexOf('Windows NT 6.2') !== -1) os = 'Windows 8';
    else if (userAgent.indexOf('Windows NT 6.1') !== -1) os = 'Windows 7';
    else os = 'Windows';
  } else if (userAgent.indexOf('Mac') !== -1) os = 'MacOS';
  else if (userAgent.indexOf('Linux') !== -1) os = 'Linux';
  else if (userAgent.indexOf('Android') !== -1) os = 'Android';
  else if (userAgent.indexOf('iOS') !== -1) os = 'iOS';
  
  // Detect Browser
  let browser = 'Unknown Browser';
  let browserVersion = '';
  if (userAgent.indexOf('Edg') !== -1) {
    browser = 'Microsoft Edge';
    browserVersion = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Chrome') !== -1) {
    browser = 'Google Chrome';
    browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Firefox') !== -1) {
    browser = 'Mozilla Firefox';
    browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Safari') !== -1) {
    browser = 'Safari';
    browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  }
  
  // Try to get computer name
  let hostname = 'Unknown';
  try {
    hostname = window.location.hostname || 'Unknown';
  } catch (e) {
    hostname = 'Unable to detect';
  }
  
  return {
    hostname,
    os,
    browser: `${browser} ${browserVersion}`,
    user_agent: userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString()
  };
};

  // Tailwind's darkMode:'class' strategy keys off `dark` on <html>, so toggle it there
  // (not on a wrapper div) or nested utilities won't see it.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const api = async (endpoint, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    // A 401 from the login routes means "bad credentials" — let the real server
    // message through. A 401 from any *other* route means the session expired.
    const isLoginRequest =
      endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/ad-login');
    if (response.status === 401 && !isLoginRequest) {
      handleLogout();
      throw new Error('Your session has expired. Please sign in again.');
    }
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  // Attachments can't go through `api()` because that helper parses JSON, and they
  // can't be plain <a href> links either: the browser wouldn't send the Authorization
  // header, and /api/attachments now requires it. So fetch with the header, then hand
  // the blob to a throwaway anchor.
  // Extensions we are willing to render in-app. Deliberately narrow:
  //  - <img> cannot execute anything, whatever the bytes actually turn out to be.
  //  - PDF renders in the browser's own sandboxed viewer.
  //  - SVG is excluded on purpose. It is XML and it can carry script.
  //  - HEIC is excluded because no browser renders it, so iPhone photos still
  //    download rather than showing a broken image.
  // Anything not on this list keeps the old download behaviour.
  const PREVIEWABLE = /\.(png|jpe?g|gif|webp|pdf)$/i;

  const fetchAttachmentBlob = async (filename) => {
    const response = await fetch(`${API_URL}/attachments/${encodeURIComponent(filename)}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (response.status === 401) {
      handleLogout();
      setError('Your session has expired. Please sign in again.');
      return null;
    }
    if (response.status === 403) {
      setError('You do not have access to that attachment.');
      return null;
    }
    if (!response.ok) {
      setError('That attachment could not be found.');
      return null;
    }
    return response.blob();
  };

  const saveBlobAs = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // The server sends Content-Disposition: attachment, but that only governs
  // navigation. fetch() ignores it, so previewing needs no server change and the
  // nosniff/attachment defense on that route stays exactly as it is.
  const openAttachment = async (filename) => {
    try {
      const blob = await fetchAttachmentBlob(filename);
      if (!blob) return;
      const name = filename.substring(filename.indexOf('-') + 1);
      const url = window.URL.createObjectURL(blob);
      if (PREVIEWABLE.test(name)) {
        setAttachmentPreview({ url, name, kind: /\.pdf$/i.test(name) ? 'pdf' : 'image' });
      } else {
        saveBlobAs(url, name);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Attachment error:', err);
      setError('Could not open attachment.');
    }
  };

  const closeAttachmentPreview = () => {
    // Revoke on close or every previewed file stays in memory for the session.
    if (attachmentPreview) window.URL.revokeObjectURL(attachmentPreview.url);
    setAttachmentPreview(null);
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await api('/settings');
      setNotifySettings(data['notifications.new_ticket']?.value || null);
      await refreshRecipientPreview();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Shows exactly who would be emailed, resolved server-side by the same function
  // that actually sends. A preview computed in the browser would be a second copy
  // of the rule, and drifting copies are what broke notifications in the first place.
  const refreshRecipientPreview = async () => {
    try {
      const [tech, fac] = await Promise.all([
        api('/settings/preview-recipients/technology'),
        api('/settings/preview-recipients/facilities')
      ]);
      setRecipientPreview({ technology: tech.recipients, facilities: fac.recipients });
    } catch (err) {
      console.error('Preview failed:', err);
    }
  };

  const saveNotifySettings = async (next) => {
    setSettingsSaving(true);
    setSettingsSaved(false);
    setError('');
    try {
      await api('/settings/notifications.new_ticket', {
        method: 'PUT',
        body: JSON.stringify({ value: next })
      });
      setNotifySettings(next);
      await refreshRecipientPreview();
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleRole = (dept, role) => {
    if (!notifySettings) return;
    const current = notifySettings[dept]?.roles || [];
    const roles = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    saveNotifySettings({
      ...notifySettings,
      [dept]: { ...notifySettings[dept], roles }
    });
  };

  const addExtraEmail = () => {
    const email = extraEmailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('That does not look like a valid email address.');
      return;
    }
    if ((notifySettings.extra_emails || []).includes(email)) {
      setError('That address is already on the list.');
      return;
    }
    saveNotifySettings({
      ...notifySettings,
      extra_emails: [...(notifySettings.extra_emails || []), email]
    });
    setExtraEmailInput('');
  };

  const removeExtraEmail = (email) => {
    saveNotifySettings({
      ...notifySettings,
      extra_emails: (notifySettings.extra_emails || []).filter((e) => e !== email)
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const isEmail = loginForm.email.includes('@');
      let data;
      if (isEmail) {
        data = await api('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email: loginForm.email, password: loginForm.password })
        });
      } else {
        data = await api('/auth/ad-login', {
          method: 'POST',
          body: JSON.stringify({ username: loginForm.email, password: loginForm.password })
        });
      }
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
	  setView(homeViewFor(data.user));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

const handleLogout = () => {
  setToken(null);
  setUser(null);
  setError('');
  setSessionWarning(false);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  setView('dashboard');
};

  // Proactive session-expiry handling driven off the JWT's own exp claim.
  // Warns ~2 minutes before expiry, then cleanly logs out at expiry — no manual
  // page refresh needed. Re-runs whenever the token changes (login / restore).
  useEffect(() => {
    if (!token) return;
    const expMs = getTokenExpiry(token);
    if (!expMs) return; // unreadable token — let the normal 401 path handle it

    const msUntilExpiry = expMs - Date.now();

    // Token already expired (e.g. a tab left open overnight) — log out now.
    if (msUntilExpiry <= 0) {
      handleLogout();
      setError('Your session has expired. Please sign in again.');
      return;
    }

    const warnLeadMs = 2 * 60 * 1000; // show the heads-up 2 minutes before expiry
    const warnTimer = setTimeout(
      () => setSessionWarning(true),
      Math.max(msUntilExpiry - warnLeadMs, 0)
    );
    const expireTimer = setTimeout(() => {
      handleLogout();
      setError('Your session has expired. Please sign in again.');
    }, msUntilExpiry);

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
    };
  }, [token]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) setUser(JSON.parse(storedUser));
  }, [token]);
  
  // Track user activity
useEffect(() => {
  const resetTimer = () => setLastActivity(Date.now());
  
  window.addEventListener('mousemove', resetTimer);
  window.addEventListener('keypress', resetTimer);
  window.addEventListener('click', resetTimer);
  window.addEventListener('scroll', resetTimer);
  
  return () => {
    window.removeEventListener('mousemove', resetTimer);
    window.removeEventListener('keypress', resetTimer);
    window.removeEventListener('click', resetTimer);
    window.removeEventListener('scroll', resetTimer);
  };
}, []);

// Check for inactivity every minute - logout after 60 minutes
useEffect(() => {
  const interval = setInterval(() => {
    const inactiveTime = Date.now() - lastActivity;
    const sixtyMinutes = 60 * 60 * 1000; // 60 minutes in milliseconds
    
    if (token && inactiveTime > sixtyMinutes) {
      handleLogout();
      setError('You have been logged out due to inactivity. Please log in again.');
    }
  }, 60000); // Check every minute
  
  return () => clearInterval(interval);
}, [token, lastActivity]);

  // `silent` skips the loading flag. A background refresh must never flip `loading`,
  // because the render swaps the whole ticket list for a short "Loading..." div — the
  // document collapses, the browser clamps scroll to the new height, and you get thrown
  // back to the top. Silent refreshes let React reconcile rows in place instead.
  const fetchTickets = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await api(`/tickets?status=${filterStatus}&department=${filterDepartment}&assigned=${filterAssigned}`);
      setTickets(data);
    } catch (err) {
      // Don't surface background failures as a page-level error; the next tick retries.
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

 const fetchMyTickets = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await api(`/tickets?assigned=me&status=${filterStatus}&department=${filterDepartment}`);
      setTickets(data);
    } catch (err) {
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const fetchTeamMembers = async (dept) => {
    try {
      const data = await api(`/users/department/${dept}`);
      setTeamMembers(data);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };
  
  const fetchAllStaff = async () => {
  try {
    const data = await api('/users/all-staff');
    setAllStaffMembers(data);
  } catch (err) {
    console.error('Error fetching staff members:', err);
  }
};

// Function to fetch comments for card popup
const fetchCardComments = async (ticketId) => {
  try {
    setCardCommentsLoading(true);
    const data = await api(`/tickets/${ticketId}/comments`);
    setCardComments(data);
  } catch (err) {
    console.error('Error fetching card comments:', err);
    setCardComments([]);
  } finally {
    setCardCommentsLoading(false);
  }
};

// Handle showing comments popup
const handleShowComments = (e, ticket) => {
  e.stopPropagation();
  setShowCommentsForTicket(ticket);
  fetchCardComments(ticket.id);
};

// Handle closing comments popup
const handleCloseComments = (e) => {
  if (e) e.stopPropagation();
  setShowCommentsForTicket(null);
  setCardComments([]);
};

useEffect(() => {
  if (token) {
    if (view === 'my-tickets') {
      fetchMyTickets();
    } else {
      fetchTickets();
    }
  }
}, [token, filterStatus, filterDepartment, filterAssigned, view]);

// Handle direct ticket links from emails
useEffect(() => {
  if (!token) return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const ticketId = urlParams.get('ticket');
  
  if (ticketId) {
    // Fetch and open the ticket
    const loadTicket = async () => {
      try {
        const ticket = await api(`/tickets/${ticketId}`);
        openTicketDetail(ticket);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      } catch (err) {
        setError('Could not load ticket. It may have been deleted or you may not have permission to view it.');
      }
    };
    
    loadTicket();
  }
}, [token]);

// Auto-refresh tickets every 60 seconds
useEffect(() => {
  if (!token || (view !== 'tickets' && view !== 'dashboard' && view !== 'my-tickets')) return;

  const interval = setInterval(() => {
    console.log('Auto-refreshing tickets...');
    if (view === 'my-tickets') {
      fetchMyTickets({ silent: true });
    } else {
      fetchTickets({ silent: true });
    }
  }, 60000); // 60 seconds

  return () => clearInterval(interval);
  // filterStatus/filterDepartment MUST be here. Without them this effect never re-runs
  // on a filter change, so the interval keeps a stale closure and 60s later re-fetches
  // with the filter values from whenever you landed on the page, silently reverting
  // whatever you'd filtered to.
}, [token, view, filterStatus, filterDepartment, filterAssigned]);

const handleCreateTicket = async () => {
  if (!newTicket.title || !newTicket.location || !newTicket.description) {
    setError('Please fill in all required fields');
    return;
  }
  setLoading(true);
  try {
    const formData = new FormData();
    
    // Add all ticket fields
    formData.append('title', newTicket.title);
    formData.append('department', newTicket.department);
    formData.append('priority', newTicket.priority);
    formData.append('location', newTicket.location);
    formData.append('description', newTicket.description);
    
    if (newTicket.requested_for_user_id) {
      formData.append('requested_for_user_id', newTicket.requested_for_user_id);
    }

    // Link a device to the ticket, when one was picked
    if (newTicket.asset_id) {
      formData.append('asset_id', newTicket.asset_id);
    }
    
    // Add device info for technology tickets
    if (newTicket.department === 'technology') {
      const deviceInfo = getDeviceInfo();
      formData.append('device_info', JSON.stringify(deviceInfo));
    }
    
    // Add attachments
    ticketAttachments.forEach(file => {
      formData.append('attachments', file);
    });
    
    const response = await fetch(`${API_URL}/tickets`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) throw new Error('Failed to create ticket');
    
    setNewTicket({ 
      title: '', 
      department: 'technology', 
      priority: 'medium', 
      location: '', 
      description: '',
      requested_for_user_id: '',
      staffSearchQuery: '',
      showStaffDropdown: false,
      asset_id: '',
      deviceSearchQuery: '',
      showDeviceDropdown: false
    });
    setTicketAttachments([]);
    setView('tickets');
    fetchTickets();
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

 
  const updateTicket = async (ticketId, updates) => {
  try {
    await api(`/tickets/${ticketId}`, { method: 'PUT', body: JSON.stringify(updates) });
    fetchTickets();
    if (selectedTicket?.id === ticketId) {
      const updated = await api(`/tickets/${ticketId}`);
      setSelectedTicket(updated);
    }
  } catch (err) {
    setError(err.message);
  }
};

const saveTicketChanges = async () => {
  if (!selectedTicket) return;
  
  // Require resolution when marking as resolved
  if (ticketDraft.status === 'resolved' && !ticketDraft.resolution?.trim()) {
    setError('Please enter a resolution before marking this ticket as resolved.');
    return;
  }
  
  setLoading(true);
  try {
    await api(`/tickets/${selectedTicket.id}`, { 
      method: 'PUT', 
      body: JSON.stringify(ticketDraft) 
    });
    
    // Refresh ticket data
    fetchTickets();
    const updated = await api(`/tickets/${selectedTicket.id}`);
    setSelectedTicket(updated);
    
    // Reset draft to match current
    setTicketDraft({
      status: updated.status,
      priority: updated.priority,
      assigned_to: updated.assigned_to || '',
      department: updated.department,
      resolution: updated.resolution || ''
    });
    
    setError(''); // Clear any errors
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const fetchComments = async (ticketId) => {
    try {
      const data = await api(`/tickets/${ticketId}/comments`);
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };
  
  const deleteTicket = async (ticketId) => {
  try {
    await api(`/tickets/${ticketId}`, { method: 'DELETE' });
    setShowDeleteConfirm(false);
    setView('tickets');
    fetchTickets();
  } catch (err) {
    setError(err.message);
  }
};

const deleteComment = async (commentId) => {
  if (!window.confirm('Are you sure you want to delete this comment?')) {
    return;
  }
  
  try {
    await api(`/comments/${commentId}`, { method: 'DELETE' });
    // Refresh comments
    fetchComments(selectedTicket.id);
  } catch (err) {
    setError(err.message);
  }
};

const fetchDeletedTickets = async () => {
  setLoading(true);
  try {
    const data = await api('/tickets/deleted/list');
    setDeletedTickets(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const restoreTicket = async (ticketId) => {
  try {
    await api(`/tickets/${ticketId}/restore`, { method: 'PUT' });
    fetchDeletedTickets();
    fetchTickets();
  } catch (err) {
    setError(err.message);
  }
};

// Fetch list of tickets that can be merged into the current ticket
const fetchMergeableTickets = async (ticketId) => {
  try {
    setMergeLoading(true);
    const data = await api(`/tickets/${ticketId}/mergeable`);
    setMergeableTickets(data);
  } catch (err) {
    console.error('Error fetching mergeable tickets:', err);
    setError(err.message);
    setMergeableTickets([]);
  } finally {
    setMergeLoading(false);
  }
};

// Handle opening merge modal
const handleOpenMergeModal = () => {
  setShowMergeModal(true);
  setSelectedMergeTickets([]);
  setMergeSearchQuery('');
  fetchMergeableTickets(selectedTicket.id);
};

// Handle closing merge modal
const handleCloseMergeModal = () => {
  setShowMergeModal(false);
  setSelectedMergeTickets([]);
  setMergeSearchQuery('');
  setMergeableTickets([]);
};

// Toggle ticket selection for merge
const toggleMergeTicketSelection = (ticketId) => {
  setSelectedMergeTickets(prev => {
    if (prev.includes(ticketId)) {
      return prev.filter(id => id !== ticketId);
    } else {
      return [...prev, ticketId];
    }
  });
};

// Handle merge tickets
const handleMergeTickets = async () => {
  if (selectedMergeTickets.length === 0) {
    setError('Please select at least one ticket to merge');
    return;
  }

  if (!window.confirm(`Are you sure you want to merge ${selectedMergeTickets.length} ticket(s) into this ticket? This action cannot be undone.`)) {
    return;
  }

  try {
    setMergeLoading(true);
    await api(`/tickets/${selectedTicket.id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourceTicketIds: selectedMergeTickets })
    });
    
    // Refresh ticket data
    const updated = await api(`/tickets/${selectedTicket.id}`);
    setSelectedTicket(updated);
    fetchComments(selectedTicket.id);
    fetchTickets();
    
    // Close modal
    handleCloseMergeModal();
    
    setError(''); // Clear any errors
    // Could add a success message here if desired
  } catch (err) {
    setError(err.message);
  } finally {
    setMergeLoading(false);
  }
};

// ============ USER MANAGEMENT FUNCTIONS ============

const fetchUsers = async () => {
  try {
    setLoading(true);
    const data = await api('/users');
    setUsers(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const openCreateUserModal = () => {
  setEditingUser(null);
  setUserForm({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: '',
    notify_new_ticket_alerts: 'role'
  });
  setShowUserModal(true);
};

const openEditUserModal = (user) => {
  setEditingUser(user);
  setUserForm({
    name: user.name,
    email: user.email,
    password: '', // Don't pre-fill password
    role: user.role,
    department: user.department || '',
    notify_new_ticket_alerts: user.notify_new_ticket_alerts || 'role'
  });
  setShowUserModal(true);
};

const closeUserModal = () => {
  setShowUserModal(false);
  setEditingUser(null);
  setUserForm({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: '',
    notify_new_ticket_alerts: 'role'
  });
};

const handleSaveUser = async () => {
  try {
    setLoading(true);
    
    if (!userForm.name || !userForm.email) {
      setError('Name and email are required');
      return;
    }
    
    if (!editingUser && !userForm.password) {
      setError('Password is required for new users');
      return;
    }

    if (editingUser) {
      // Update existing user
      const updateData = {
        name: userForm.name,
        email: userForm.email,
        role: userForm.role,
        department: userForm.department || null,
        notify_new_ticket_alerts: userForm.notify_new_ticket_alerts || 'role'
      };
      
      // Only include password if it was changed
      if (userForm.password) {
        updateData.password = userForm.password;
      }
      
      await api(`/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
    } else {
      // Create new user
      await api('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
          department: userForm.department || null
        })
      });
    }
    
    closeUserModal();
    fetchUsers();
    setError('');
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const handleDeleteUser = async (userId) => {
  try {
    setLoading(true);
    await api(`/users/${userId}`, { method: 'DELETE' });
    setShowDeleteUserConfirm(null);
    fetchUsers();
    setError('');
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const getRoleBadgeColor = (role) => {
  switch (role) {
    case 'admin': return 'bg-purple-100 text-purple-800';
    case 'tech': return 'bg-blue-100 text-blue-800';
    case 'tech-admin': return 'bg-blue-200 text-blue-900';
    case 'facilities': return 'bg-green-100 text-green-800';
    case 'facilities-admin': return 'bg-green-200 text-green-900';
    case 'custodian': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-n-100 text-n-800';
  }
};

const getRoleDisplayName = (role) => {
  switch (role) {
    case 'admin': return 'Admin';
    case 'tech': return 'Tech Team';
    case 'tech-admin': return 'Tech Admin';
    case 'facilities': return 'Facilities';
    case 'facilities-admin': return 'Facilities Admin';
    case 'custodian': return 'Custodian';
    case 'staff': return 'Staff';
    default: return role;
  }
};

const fetchDevices = async () => {
  try {
    setLoading(true);
    const data = await api('/assets');
    setDevices(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const openDevice = async (id) => {
  setSelectedDevice(null);
  setDeviceTickets([]);
  setView('device-detail');
  try {
    setLoading(true);
    const data = await api(`/assets/${id}`);
    setSelectedDevice(data.device);
    setDeviceTickets(data.tickets || []);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

// Save just the assigned user for the open device. The nightly sync never
// overwrites assigned_user, so this edit is permanent.
// Build an .rdp file in the browser and hand it to the OS, which opens the
// Remote Desktop client pointed at this machine. No backend involved.
// NOTE: RDP disconnects (locks) whoever is signed in at the console — their
// session stays alive and resumes when they log back in, but their screen locks.
const launchRdp = (device) => {
  const target = device?.hostname;
  if (!target) {
    setError('This device has no hostname, so RDP cannot be launched.');
    return;
  }
  // If bare hostnames do not resolve for you, append your AD DNS suffix here,
  // e.g. `${target}.yourdomain.local`
  const rdpFile = [
    `full address:s:${target}`,
    'prompt for credentials:i:1',
    'screen mode id:i:2',
    'redirectclipboard:i:1',
    'redirectprinters:i:0',
    'authentication level:i:2',
    'autoreconnection enabled:i:1',
  ].join('\r\n');

  const blob = new Blob([rdpFile], { type: 'application/x-rdp' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${target}.rdp`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Shadow the signed-in user's session — you see (and can control) their actual
// desktop without locking them out, unlike RDP. Shadowing needs the target's
// session ID, which varies per machine, so the generated .cmd looks it up with
// qwinsta first and then launches mstsc /shadow.
//
// Requires on the target (see GPO + firewall setup):
//   - "Set rules for remote control of Remote Desktop Services user sessions"
//   - Remote Desktop - Shadow (TCP-In) firewall rule
//   - Local admin rights on the target
const launchShadow = (device) => {
  const target = device?.hostname;
  if (!target) {
    setError('This device has no hostname, so a shadow session cannot be started.');
    return;
  }

  // Set to match your remote-control GPO:
  //   true  -> GPO is "Full Control WITHOUT user's permission" (no prompt)
  //   false -> GPO is "Full Control WITH user's permission" (user must accept)
  // Mismatching these is the most common reason a shadow is refused.
  const NO_CONSENT_PROMPT = true;
  const shadowArgs = NO_CONSENT_PROMPT ? '/control /noConsentPrompt' : '/control';

  const cmdFile = [
    '@echo off',
    'setlocal enabledelayedexpansion',
    '',
    'rem --- self-elevate: relaunch this script as admin if it is not already ---',
    'net session >nul 2>&1',
    'if %errorlevel% neq 0 (',
    '    echo Requesting administrator rights ...',
    '    powershell -NoProfile -Command "Start-Process -FilePath \'%~f0\' -Verb RunAs"',
    '    exit /b',
    ')',
    '',
    `set "TARGET=${target}"`,
    `set "SHADOWARGS=${shadowArgs}"`,
    '',
    'echo ============================================',
    'echo  Shadow session: %TARGET%',
    'echo ============================================',
    'echo.',
    '',
    'echo [1/4] Checking that %TARGET% responds ...',
    'ping -n 1 -w 1000 %TARGET% >nul 2>&1',
    'if errorlevel 1 (',
    '    echo       No ping reply - continuing anyway ^(ICMP is often blocked^).',
    ') else (',
    '    echo       OK - host responded.',
    ')',
    'echo.',
    '',
    'echo [2/4] Querying sessions on %TARGET% ...',
    'qwinsta /server:%TARGET% >nul 2>&1',
    'if errorlevel 1 (',
    '    echo       FAILED - the session query was refused or timed out.',
    '    echo.',
    '    echo       Usually one of:',
    '    echo         - Your Windows account is not a local admin on %TARGET%',
    '    echo           ^(if you use a separate admin account, right-click this file',
    '    echo            and choose "Run as different user"^)',
    '    echo         - RPC ^(TCP 135^) blocked by the firewall on the target',
    '    echo         - Machine is offline, or the name does not resolve from here',
    '    goto :end',
    ')',
    'echo       OK - sessions queried.',
    'echo.',
    '',
    'echo [3/4] Finding the active session ...',
    'set "SESSID="',
    `for /f "skip=1 tokens=1,2,3,4" %%a in ('qwinsta /server:%TARGET% 2^>nul') do (`,
    '    if /i "%%d"=="Active" set "SESSID=%%c"',
    '    if /i "%%c"=="Active" set "SESSID=%%b"',
    ')',
    'if not defined SESSID (',
    '    echo       FAILED - no active session found.',
    '    echo       Nobody appears to be signed in at %TARGET%.',
    '    echo.',
    '    echo       Current sessions:',
    '    qwinsta /server:%TARGET%',
    '    goto :end',
    ')',
    'echo       OK - active session ID is !SESSID!.',
    'echo.',
    '',
    'echo [4/4] Opening shadow session ...',
    'echo       mstsc /shadow:!SESSID! /v:%TARGET% %SHADOWARGS%',
    'echo.',
    'start /wait "" mstsc /shadow:!SESSID! /v:%TARGET% %SHADOWARGS%',
    'if errorlevel 1 (',
    '    echo       FAILED - mstsc exited with code !errorlevel!.',
    '    echo.',
    '    echo       Usually one of:',
    '    echo         - "Remote Desktop - Shadow ^(TCP-In^)" firewall rule not enabled on target',
    '    echo         - Remote-control GPO has not reached the target yet',
    '    echo           ^(run gpupdate /force on it, then retry^)',
    '    echo         - /noConsentPrompt used while the GPO still requires consent',
    '    echo           ^(or the user declined the prompt^)',
    '    goto :end',
    ')',
    'echo       Shadow session closed.',
    '',
    ':end',
    'echo.',
    'pause',
  ].join('\r\n');

  const blob = new Blob([cmdFile], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `shadow-${target}.cmd`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const saveAssigned = async () => {
  setSavingAssigned(true);
  try {
    const updated = await api(`/assets/${selectedDevice.id}`, {
      method: 'PUT',
      body: JSON.stringify({ assigned_user: assignedInput.trim() }),
    });
    setSelectedDevice(updated);
    setDevices(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
    setEditingAssigned(false);
  } catch (err) {
    setError(err.message);
  } finally {
    setSavingAssigned(false);
  }
};

// Attach (or clear) the device linked to an existing ticket.
const linkTicketDevice = async (assetId) => {
  try {
    await api(`/tickets/${selectedTicket.id}/device`, {
      method: 'PUT',
      body: JSON.stringify({ asset_id: assetId }),
    });
    const refreshed = await api(`/tickets/${selectedTicket.id}`);
    setSelectedTicket(refreshed);
    setShowTicketDevicePicker(false);
    setTicketDeviceSearch('');
    fetchTickets();
  } catch (err) {
    setError(err.message);
  }
};

const filteredDevices = devices.filter(d => {
  const q = deviceSearch.trim().toLowerCase();
  if (!q) return true;
  return [d.asset_tag, d.hostname, d.model, d.assigned_user]
    .some(v => (v || '').toLowerCase().includes(q));
});

const filteredUsers = users.filter(u => {
  const matchesSearch = u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        u.email.toLowerCase().includes(userSearchQuery.toLowerCase());
  const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
  return matchesSearch && matchesRole;
});

// ============ END USER MANAGEMENT FUNCTIONS ============

  const handleAddComment = async () => {
  if (!commentText.trim()) return;
  try {
    if (commentAttachments.length > 0) {
      // Use FormData for attachments
      const formData = new FormData();
      formData.append('text', commentText);
      commentAttachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await fetch(`${API_URL}/tickets/${selectedTicket.id}/comments/with-attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to add comment');
    } else {
      // No attachments, use regular API
      await api(`/tickets/${selectedTicket.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text: commentText })
      });
    }
    
    setCommentText('');
    setCommentAttachments([]);
    fetchComments(selectedTicket.id);
  } catch (err) {
    setError(err.message);
  }
};

// The dashboard hands back a target view plus the filters it should land on,
// so clicking "Unassigned" arrives at a filtered list instead of the top of
// everything.
const handleDashboardNavigate = (target, opts = {}) => {
  // Reset anything the caller didn't ask for. Setting only what's passed leaves
  // stale filters on: click Unassigned, then click Open, and you'd still be
  // filtered to unassigned while the card implies you're seeing all open work.
  setFilterStatus(opts.status !== undefined ? opts.status : 'all');
  setSortBy(opts.sortBy !== undefined ? opts.sortBy : 'date');
  setFilterAssigned(opts.assigned !== undefined ? opts.assigned : 'all');
  setView(target);
};

const openUserDetail = (id) => {
  setSelectedUserId(id);
  setView('user-detail');
};

const openTicketById = async (id) => {
  try {
    const ticket = await api(`/tickets/${id}`);
    openTicketDetail(ticket);
  } catch (err) {
    setError('Could not open that ticket.');
  }
};

const openTicketDetail = async (ticket) => {
  setSelectedTicket(ticket);
  setView('detail');
  fetchComments(ticket.id);
  fetchTeamMembers(ticket.department);
  
  // Initialize draft with current values
  setTicketDraft({
    status: ticket.status,
    priority: ticket.priority,
    assigned_to: ticket.assigned_to || '',
    department: ticket.department,
    resolution: ticket.resolution || ''
  });
};

  const filteredTickets = tickets.filter(ticket => {
  const searchMatch = searchQuery === '' || 
    ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(searchQuery.toLowerCase()));
  return searchMatch;
}).sort((a, b) => {
  if (sortBy === 'status') {
    // Define status priority: open=1, in-progress=2, resolved=3
    const statusOrder = { 'open': 1, 'in-progress': 2, 'resolved': 3 };
    return statusOrder[a.status] - statusOrder[b.status];
  }
  // Default: sort by date (newest first)
  return new Date(b.created_at) - new Date(a.created_at);
});

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    urgent: tickets.filter(t => t.priority === 'urgent' && t.status !== 'resolved').length,
    high: tickets.filter(t => t.priority === 'high' && t.status !== 'resolved').length
};

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-n-50 to-n-100 flex items-center justify-center p-4">
        <div className="bg-surface p-8 rounded-2xl shadow-xl w-full max-w-md">
          <div className="text-center mb-8">
            <img src={SCHOOL_LOGO} alt={`${SCHOOL_NAME} Logo`} className="h-20 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-n-900">{SCHOOL_NAME}</h1>
            <p className="text-n-600 mt-2">Help Desk Portal</p>
          </div>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-n-700 mb-1">Username</label>
              <input
                type="text"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-n-300 rounded-lg focus:ring-2 focus:border-transparent"
                placeholder="username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-n-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
                className="w-full px-3 py-2 border border-n-300 rounded-lg focus:ring-2 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={handleLogin}
              disabled={loading}
              style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
              className="w-full text-white py-2 rounded-lg transition font-medium disabled:bg-n-400"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Navigation
  //
  // Previously each nav button repeated its own role test and its own 140-char
  // className. Adding a page meant copy/pasting both and hoping you got the
  // gating right. The gate is now defined once and the items are data.
  //
  // NOTE: this mirrors, and does not replace, the server-side requireRole()
  // checks from pass 2. Hiding a button is a courtesy, not a permission.
  // ---------------------------------------------------------------------------
  const isAdmin = user?.role === 'admin';
  // Who can see and use the device inventory (incl. Shadow/RDP buttons).
  const isTechTeam = ['admin', 'tech', 'tech-admin'].includes(user?.role);
  const isStaff =
    (isAdmin ||
      ['tech', 'tech-admin', 'facilities', 'facilities-admin'].includes(user?.role) ||
      ['technology', 'facilities'].includes(user?.department)) &&
    user?.role !== 'custodian';

  const navItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      show: isStaff,
      onSelect: () => { setView('dashboard'); setFilterStatus('all'); setFilterDepartment('all'); },
    },
    {
      key: 'tickets',
      // For a teacher this list only ever contains their own tickets, so
      // calling it 'Tickets' undersells it and calling it 'All Tickets' would
      // be a lie.
      label: isStaff ? 'Tickets' : 'My Tickets',
      icon: Ticket,
      show: true,
      onSelect: () => { setView('tickets'); setSortBy('status'); setFilterStatus('all'); },
    },
    {
      key: 'my-tickets',
      label: 'My Tickets',
      icon: Inbox,
      show: isStaff,
      onSelect: () => { setView('my-tickets'); setFilterStatus('all'); },
    },
    {
      key: 'deleted',
      label: 'Deleted',
      icon: Trash2,
      show: isStaff,
      onSelect: () => { setView('deleted'); fetchDeletedTickets(); },
    },
    {
      key: 'users',
      label: 'Users',
      icon: UsersIcon,
      show: isAdmin,
      onSelect: () => { setView('users'); fetchUsers(); },
    },
    {
      key: 'devices',
      label: 'Devices',
      icon: Laptop,
      show: isTechTeam,
      onSelect: () => { setView('devices'); fetchDevices(); },
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: SettingsIcon,
      show: isAdmin,
      onSelect: () => { setView('settings'); fetchSettings(); },
    },
  ].filter(item => item.show);

  // 'create' and 'detail' are reachable from anywhere and own no nav item, so the
  // fallback keeps the top bar honest rather than showing a stale page title.
  const pageTitle = navItems.find(i => i.key === view)?.label
    || { create: 'New Ticket', detail: 'Ticket Details', 'device-detail': 'Device Details' }[view]
    || 'Help Desk';

return (
  <div className="min-h-screen bg-n-50">
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
      {/* Backdrop for the mobile drawer. md:hidden so it can never trap a desktop click. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/*
        The sidebar is deliberately PRIMARY_COLOR in both light and dark mode.
        That is what retires the whole class of contrast bugs we kept fixing in
        pass 1: on a surface that is always dark green, white text is correct by
        construction instead of being an exception the theme has to remember.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-200 ease-out md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: PRIMARY_COLOR }}
      >
        {/* Brand. With a full 256px column the title finally has room to wrap to
            two lines, so no whitespace-nowrap and nothing gets clipped. */}
        <div className="flex items-start gap-3 px-4 py-5 border-b border-white/10">
          <button
            onClick={() => { setView(homeViewFor(user)); setFilterStatus('all'); setFilterDepartment('all'); setSidebarOpen(false); }}
            className="flex items-start gap-3 text-left min-w-0 flex-1"
            title="Go home"
          >
            <img src={SCHOOL_LOGO} alt="" className="h-10 w-10 object-contain bg-white rounded p-1 flex-shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-bold text-white leading-snug">{SCHOOL_NAME}</span>
              <span className="block text-xs text-white/70 leading-snug mt-0.5">Help Desk Portal</span>
            </span>
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-1 text-white/70 hover:text-white transition flex-shrink-0"
            aria-label="Close navigation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map(({ key, label, icon: Icon, onSelect }) => (
            <button
              key={key}
              onClick={() => { onSelect(); setSidebarOpen(false); }}
              aria-current={view === key ? 'page' : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                view === key
                  ? 'bg-white/20 text-white'
                  : 'text-white/75 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </nav>

        {/* Identity + sign out live at the foot of the sidebar, which is where
            people look for them, and frees the top bar for page actions. */}
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate capitalize">{user?.role?.replace('-', ' ')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-white/70 hover:text-white transition flex-shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main column, inset by the sidebar width on md+ only. */}
      <div className="md:pl-64">
        <header className="sticky top-0 z-30 bg-pv-100 dark:bg-surface border-b border-n-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {/* The nav used to be hidden md:flex with no fallback, so on a phone
                    there was no way to reach any page at all. This is that fix. */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="md:hidden p-2 -ml-2 text-n-600 hover:text-n-900 transition"
                  aria-label="Open navigation"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-lg font-semibold text-n-900 truncate">{pageTitle}</h1>
              </div>

              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={async () => {
                    setIsRefreshing(true);
                    if (view === 'my-tickets') {
                      await fetchMyTickets({ silent: true });
                    } else {
                      await fetchTickets({ silent: true });
                    }
                    setTimeout(() => setIsRefreshing(false), 1000);
                  }}
                  className="p-2 text-n-600 hover:text-n-900 transition disabled:opacity-50"
                  title="Refresh tickets"
                  aria-label="Refresh tickets"
                  disabled={isRefreshing}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ animation: isRefreshing ? 'spin 1s ease-in-out' : 'none' }}
                  >
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </button>
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="p-2 text-n-600 hover:text-n-900 transition"
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setView('create')}
                  style={{ backgroundColor: PRIMARY_COLOR }}
                  className="ml-1 px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Ticket</span>
                </button>
              </div>
            </div>
          </div>
        </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {sessionWarning && (
          <div className="bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 flex-shrink-0" />
            <span>Your session is about to expire. Please save any work — you'll need to sign in again shortly.</span>
            <button onClick={() => setSessionWarning(false)} className="ml-auto font-bold">×</button>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button onClick={() => setError('')} className="float-right font-bold">×</button>
          </div>
        )}

{view === 'dashboard' && (
  <Dashboard
    token={token}
    user={user}
    onOpenTicket={openTicketDetail}
    onNavigate={handleDashboardNavigate}
    onOpenUser={openUserDetail}
  />
)}

        {view === 'create' && (
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <button onClick={() => setView(homeViewFor(user))} className="text-n-600 hover:text-n-900 font-medium text-sm mb-4">← Back</button>
              <h2 className="text-2xl font-bold text-n-900">Create New Ticket</h2>
              <p className="text-n-600 mt-1">Submit a request for assistance</p>
            </div>
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
              <div className="space-y-6">
			  {/* Only show for tech/facilities team members - NOT custodians */}
{(user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || 
  user?.role === 'facilities' || user?.role === 'facilities-admin' ||
  user?.department === 'technology' || user?.department === 'facilities') && user?.role !== 'custodian' && (
  <div className="relative">
    <label className="block text-sm font-medium text-n-700 mb-2">
      Create Ticket For
    </label>
    <input
      type="text"
      value={newTicket.staffSearchQuery || ''}
      onChange={(e) => {
        setNewTicket({ 
          ...newTicket, 
          staffSearchQuery: e.target.value,
          showStaffDropdown: true 
        });
      }}
      onFocus={() => {
        if (allStaffMembers.length === 0) {
          fetchAllStaff();
        }
        setNewTicket({ ...newTicket, showStaffDropdown: true });
      }}
      onBlur={() => {
        // Delay to allow click on dropdown item
        setTimeout(() => {
          setNewTicket({ ...newTicket, showStaffDropdown: false });
        }, 300);
      }}
      placeholder="Search staff member or leave blank for yourself..."
      className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
    />
    
    {/* Dropdown results */}
    {newTicket.showStaffDropdown && newTicket.staffSearchQuery && (
      <div className="absolute z-10 w-full mt-1 bg-surface border border-n-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {allStaffMembers
          .filter(staff => 
            staff.name.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase()) ||
            staff.email.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase())
          )
          .slice(0, 10)
          .map(staff => (
            <div
              key={staff.id}
              onMouseDown={() => {
                setNewTicket({
                  ...newTicket,
                  requested_for_user_id: staff.id,
                  staffSearchQuery: staff.name,
                  showStaffDropdown: false
                });
              }}
              className="px-4 py-2 hover:bg-n-100 cursor-pointer"
            >
              <div className="font-medium text-n-900">{staff.name}</div>
              <div className="text-xs text-n-500">{staff.email}</div>
            </div>
          ))}
        {allStaffMembers.filter(staff => 
          staff.name.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase()) ||
          staff.email.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase())
        ).length === 0 && (
          <div className="px-4 py-3 text-n-500 text-sm">No staff members found</div>
        )}
      </div>
    )}
    
    {/* Show selected staff member */}
    {newTicket.requested_for_user_id && !newTicket.staffSearchQuery && (
      <div className="mt-2 text-sm text-n-600">
        Creating ticket for: <span className="font-medium">
          {allStaffMembers.find(s => s.id === parseInt(newTicket.requested_for_user_id))?.name || 'Selected user'}
        </span>
        <button
          type="button"
          onClick={() => setNewTicket({ ...newTicket, requested_for_user_id: '', staffSearchQuery: '' })}
          className="ml-2 text-red-600 hover:text-red-800"
        >
          Clear
        </button>
      </div>
    )}
    
    <p className="text-xs text-n-500 mt-1">
      Type to search for a staff member, or leave blank to create for yourself
    </p>
  </div>
)}
{/* Device picker — tech team only, matching the device endpoints */}
{isTechTeam && (
  <div className="relative">
    <label className="block text-sm font-medium text-n-700 mb-2">
      Related Device
    </label>
    <input
      type="text"
      value={newTicket.deviceSearchQuery || ''}
      onChange={(e) => {
        setNewTicket({
          ...newTicket,
          deviceSearchQuery: e.target.value,
          showDeviceDropdown: true
        });
      }}
      onFocus={() => {
        if (devices.length === 0) {
          fetchDevices();
        }
        setNewTicket({ ...newTicket, showDeviceDropdown: true });
      }}
      onBlur={() => {
        setTimeout(() => {
          setNewTicket({ ...newTicket, showDeviceDropdown: false });
        }, 300);
      }}
      placeholder="Search by hostname, tag, or assigned user..."
      className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
    />

    {newTicket.showDeviceDropdown && newTicket.deviceSearchQuery && (
      <div className="absolute z-10 w-full mt-1 bg-surface border border-n-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {devices
          .filter(d => {
            const q = newTicket.deviceSearchQuery.toLowerCase();
            return [d.hostname, d.asset_tag, d.model, d.assigned_user]
              .some(v => (v || '').toLowerCase().includes(q));
          })
          .slice(0, 10)
          .map(d => (
            <div
              key={d.id}
              onMouseDown={() => {
                setNewTicket({
                  ...newTicket,
                  asset_id: d.id,
                  deviceSearchQuery: d.hostname || d.asset_tag || `Device #${d.id}`,
                  showDeviceDropdown: false
                });
              }}
              className="px-4 py-2 hover:bg-n-100 cursor-pointer"
            >
              <div className="font-medium text-n-900">{d.hostname || d.asset_tag || `Device #${d.id}`}</div>
              <div className="text-xs text-n-500">{[d.model, d.assigned_user].filter(Boolean).join(' · ') || '—'}</div>
            </div>
          ))}
        {devices.filter(d => {
          const q = newTicket.deviceSearchQuery.toLowerCase();
          return [d.hostname, d.asset_tag, d.model, d.assigned_user]
            .some(v => (v || '').toLowerCase().includes(q));
        }).length === 0 && (
          <div className="px-4 py-3 text-n-500 text-sm">No devices found</div>
        )}
      </div>
    )}

    {newTicket.asset_id && (
      <div className="mt-2 text-sm text-n-600">
        Linked device: <span className="font-medium">{newTicket.deviceSearchQuery}</span>
        <button
          type="button"
          onClick={() => setNewTicket({ ...newTicket, asset_id: '', deviceSearchQuery: '' })}
          className="ml-2 text-red-600 hover:text-red-800"
        >
          Clear
        </button>
      </div>
    )}

    <p className="text-xs text-n-500 mt-1">
      Optional — attach the machine this ticket is about
    </p>
  </div>
)}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-n-700 mb-2">Location *</label>
                    <input type="text" value={newTicket.location} onChange={(e) => setNewTicket({ ...newTicket, location: e.target.value })} className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" placeholder="Room number or area" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n-700 mb-2">Department *</label>
                    {user?.role === 'custodian' ? (
                      <div className="w-full px-4 py-2.5 border border-n-300 rounded-lg bg-n-50 text-n-600">
                        Facilities
                      </div>
                    ) : (
                      <select value={newTicket.department} onChange={(e) => setNewTicket({ ...newTicket, department: e.target.value })} className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent">
                        <option value="technology">Technology</option>
                        <option value="facilities">Facilities</option>
                      </select>
                    )}
                  </div>
                </div>
                {(user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || 
                  user?.role === 'facilities' || user?.role === 'facilities-admin' ||
                  user?.department === 'technology' || user?.department === 'facilities') && user?.role !== 'custodian' ? (
  <div>
    <label className="block text-sm font-medium text-n-700 mb-2">Priority *</label>
    <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })} className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent">
      <option value="low">Low</option>
      <option value="medium">Medium</option>
      <option value="high">High</option>
      <option value="urgent">Urgent</option>
    </select>
  </div>
) : (
  <div>
    <label className="block text-sm font-medium text-n-700 mb-2">Priority</label>
    <div className="w-full px-4 py-2.5 border border-n-300 rounded-lg bg-n-50 text-n-600">
      Medium (Default)
    </div>
    <p className="text-xs text-n-500 mt-1">Priority will be set to Medium. {user?.role === 'custodian' ? 'Facilities' : 'Tech'} team will adjust if needed.</p>
  </div>
)}
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Issue Title *</label>
                  <input type="text" value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" placeholder="Brief description of the issue" />
                </div>
				  <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Attachments (Optional)</label>
                  <input 
                    type="file" 
                    multiple 
                    onChange={(e) => setTicketAttachments(Array.from(e.target.files))}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                  />
                  {ticketAttachments.length > 0 && (
                    <div className="mt-2 text-sm text-n-600">
                      {ticketAttachments.length} file(s) selected
                    </div>
                  )}
                  <p className="text-xs text-n-500 mt-1">Max 5 files, 10MB each</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Detailed Description *</label>
                  <textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} rows="5" className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" placeholder="Provide as much detail as possible about the issue..." />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={handleCreateTicket} disabled={loading} style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}} className="px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 transition">{loading ? 'Submitting...' : 'Submit Ticket'}</button>
                  <button onClick={() => setView(homeViewFor(user))} className="px-6 py-2.5 bg-n-100 text-n-700 rounded-lg hover:bg-n-200 transition font-medium">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'tickets' && (
          <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-n-900">All Tickets</h2>
              <p className="text-n-600 mt-1">Manage and track support requests</p>
            </div>
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-n-400" />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tickets..." className="w-full pl-10 pr-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} className="px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                    <option value="all">All Departments</option>
                    <option value="technology">Technology</option>
                    <option value="facilities">Facilities</option>
                  </select>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                    <option value="all">All Status</option>
                    <option value="unresolved">Unresolved</option>
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <select value={filterAssigned} onChange={(e) => setFilterAssigned(e.target.value)} className="px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                    <option value="all">Anyone</option>
                    <option value="me">Assigned to me</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">Loading tickets...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-12 text-center">
                  <div className="bg-n-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-n-400" /></div>
                  <p className="text-n-500 text-lg font-medium">No tickets found</p>
                  <p className="text-n-400 mt-1">Try adjusting your filters or create a new ticket</p>
                  <button onClick={() => setView('create')} style={{backgroundColor: PRIMARY_COLOR}} className="mt-6 px-6 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium">Create New Ticket</button>
                </div>
              ) : (
                <div className="bg-surface rounded-xl border border-n-200 divide-y divide-n-200 overflow-hidden">
                  {filteredTickets.map(ticket => (
                    <TicketRow
                      key={ticket.id}
                      ticket={ticket}
                      onOpen={openTicketDetail}
                      onComments={handleShowComments}
                    />
                  ))}
                </div>
                            )}
            </div>
          </div>
        )}
		
	{view === 'my-tickets' && (
   <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-n-900">My Assigned Tickets</h2>
      <p className="text-n-600 mt-1">Tickets currently assigned to you</p>
    </div>
    <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-4 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-n-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tickets..." className="w-full pl-10 pr-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" />
          </div>
        </div>
        <div className="flex gap-3">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); }} className="px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>
    </div>
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-12">Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-12 text-center">
          <div className="bg-n-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-n-400" /></div>
          <p className="text-n-500 text-lg font-medium">No tickets assigned to you</p>
          <p className="text-n-400 mt-1">Tickets assigned to you will appear here</p>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-n-200 divide-y divide-n-200 overflow-hidden">
          {filteredTickets.map(ticket => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onOpen={openTicketDetail}
              onComments={handleShowComments}
              showAssignee={false}
            />
          ))}
        </div>
      )}
    </div>
  </div>
)}
	
		{view === 'deleted' && (
          <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
            <div className="mb-6">
              <button onClick={() => setView('tickets')} className="text-n-600 hover:text-n-900 font-medium text-sm mb-4">← Back to Tickets</button>
              <h2 className="text-2xl font-bold text-n-900">Deleted Tickets</h2>
              <p className="text-n-600 mt-1">View and restore deleted tickets</p>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">Loading deleted tickets...</div>
              ) : deletedTickets.length === 0 ? (
                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-12 text-center">
                  <div className="bg-n-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-n-400" /></div>
                  <p className="text-n-500 text-lg font-medium">No deleted tickets</p>
                  <p className="text-n-400 mt-1">Deleted tickets will appear here</p>
                </div>
              ) : (
                deletedTickets.map(ticket => (
                  <div key={ticket.id} className="bg-surface rounded-xl shadow-sm border border-n-200 p-5 hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-n-900">{ticket.title}</h3>
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-n-600">
                          <div className="flex items-center gap-2"><span className="text-n-400">Dept:</span><span className="font-medium">{ticket.department === 'technology' ? 'Tech' : 'Facilities'}</span></div>
                          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-n-400" /><span>{ticket.location}</span></div>
                          <div className="flex items-center gap-2"><User className="w-4 h-4 text-n-400" /><span>{ticket.submitted_by_name}</span></div>
                          <div className="flex items-center gap-2"><span className="text-n-400">Deleted:</span><span>{new Date(ticket.deleted_at).toLocaleDateString()}</span></div>
                        </div>
                      </div>
                      <button 
                        onClick={() => restoreTicket(ticket.id)}
                        style={{backgroundColor: PRIMARY_COLOR}}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium whitespace-nowrap"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ============ USERS MANAGEMENT VIEW ============ */}
        {view === 'settings' && user?.role === 'admin' && (
          <div className="max-w-4xl">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-n-900">Settings</h2>
              <p className="text-n-600 mt-1 text-sm">Changes save immediately and apply to the next ticket.</p>
            </div>

            {loading || !notifySettings ? (
              <div className="text-center py-12 text-n-600">Loading settings...</div>
            ) : (
              <div className="space-y-6">

                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-n-900">New ticket alerts</h3>
                      <p className="text-sm text-n-600 mt-1">
                        Who gets emailed when a ticket is created, whether from the web or by email.
                        This does not affect replies, status changes, or comments on a person's own tickets.
                      </p>
                    </div>
                    {settingsSaving && <span className="text-xs text-n-500 whitespace-nowrap mt-1">Saving...</span>}
                    {settingsSaved && <span className="text-xs text-green-600 whitespace-nowrap mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Saved</span>}
                  </div>

                  {['technology', 'facilities'].map((dept) => (
                    <div key={dept} className="mt-6 pt-6 border-t border-n-200 first:border-t-0 first:mt-4 first:pt-0">
                      <h4 className="text-sm font-semibold text-n-900 capitalize mb-3">{dept} tickets</h4>
                      <div className="flex flex-wrap gap-2">
                        {(dept === 'technology'
                          ? ['tech', 'tech-admin', 'admin', 'staff']
                          : ['facilities', 'facilities-admin', 'custodian', 'admin', 'staff']
                        ).map((role) => {
                          const on = (notifySettings[dept]?.roles || []).includes(role);
                          return (
                            <button
                              key={role}
                              type="button"
                              disabled={settingsSaving}
                              onClick={() => toggleRole(dept, role)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition disabled:opacity-50 ${
                                on
                                  ? 'text-white border-transparent'
                                  : 'bg-surface text-n-600 border-n-300 hover:border-n-400'
                              }`}
                              style={on ? { backgroundColor: PRIMARY_COLOR } : {}}
                            >
                              {role}
                            </button>
                          );
                        })}
                      </div>

                      <div className="mt-3 bg-n-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-n-700 mb-1">
                          Currently emails {recipientPreview[dept].length} {recipientPreview[dept].length === 1 ? 'person' : 'people'}
                        </p>
                        {recipientPreview[dept].length === 0 ? (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Nobody will be notified of new {dept} tickets.
                          </p>
                        ) : (
                          <p className="text-xs text-n-600 break-words">{recipientPreview[dept].join(', ')}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <h3 className="text-lg font-semibold text-n-900">Additional recipients</h3>
                  <p className="text-sm text-n-600 mt-1">
                    Extra addresses that get new ticket alerts. Useful for a shared inbox or a distribution list.
                  </p>

                  <div className="flex gap-2 mt-4">
                    <input
                      type="email"
                      value={extraEmailInput}
                      onChange={(e) => setExtraEmailInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtraEmail(); } }}
                      placeholder="name@pvrhs.org"
                      className="flex-1 px-4 py-2 border border-n-300 rounded-lg bg-surface text-n-900 text-sm focus:ring-2 focus:ring-n-400"
                    />
                    <button
                      type="button"
                      onClick={addExtraEmail}
                      disabled={settingsSaving}
                      style={{ backgroundColor: PRIMARY_COLOR }}
                      className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>

                  {(notifySettings.extra_emails || []).length === 0 ? (
                    <p className="text-sm text-n-500 mt-4">No additional recipients.</p>
                  ) : (
                    <div className="mt-4 space-y-2">
                      {notifySettings.extra_emails.map((email) => (
                        <div key={email} className="flex items-center justify-between bg-n-50 rounded-lg px-3 py-2">
                          <span className="text-sm text-n-800">{email}</span>
                          <button
                            type="button"
                            onClick={() => removeExtraEmail(email)}
                            disabled={settingsSaving}
                            className="text-n-400 hover:text-red-600 transition disabled:opacity-50"
                            aria-label={`Remove ${email}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <h3 className="text-lg font-semibold text-n-900">Per-person overrides</h3>
                  <p className="text-sm text-n-600 mt-1">
                    Anyone set to <strong>Always</strong> gets every new ticket alert regardless of the role rules above.
                    Set on each person in the Users tab.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setView('users'); fetchUsers(); }}
                    className="mt-4 px-4 py-2 border border-n-300 rounded-lg text-sm font-medium text-n-700 hover:bg-n-50 transition"
                  >
                    Go to Users
                  </button>
                </div>

              </div>
            )}
          </div>
        )}

        {view === 'devices' && (
          <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-n-900">Devices</h2>
              <p className="text-n-600 mt-1">Windows machines synced from Intune</p>
            </div>

            {/* Search Bar */}
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-n-400" />
                <input
                  type="text"
                  placeholder="Search by tag, hostname, model, or user..."
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                />
              </div>
            </div>

            {/* Devices Count */}
            <div className="mb-4 text-sm text-n-600">
              Showing {filteredDevices.length} of {devices.length} devices
            </div>

            {/* Devices Table */}
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 overflow-hidden">
              {loading ? (
                <div className="text-center py-12">Loading devices...</div>
              ) : filteredDevices.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="bg-n-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Laptop className="w-8 h-8 text-n-400" />
                  </div>
                  <p className="text-n-500 text-lg font-medium">No devices found</p>
                  <p className="text-n-400 mt-1">Try adjusting your search</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-n-50 border-b border-n-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Assigned To</th>
                        <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">OS Build</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Last Check-in</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-n-200">
                      {filteredDevices.map(d => (
                        <tr key={d.id} onClick={() => openDevice(d.id)} className="hover:bg-n-50 transition cursor-pointer">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-n-200 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                                <Laptop className="w-5 h-5 text-n-600" />
                              </div>
                              <div>
                                <div className="font-medium text-n-900">{d.hostname || d.asset_tag || '—'}</div>
                                <div className="text-xs text-n-500">{d.model || d.manufacturer || ''}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-n-600 break-all">{d.assigned_user || '—'}</td>
                          <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-n-600">{d.os_build || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-n-600">
                            {d.last_checkin ? new Date(d.last_checkin).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const c = {
                                in_service: 'bg-green-100 text-green-800',
                                spare: 'bg-blue-100 text-blue-800',
                                repair: 'bg-yellow-100 text-yellow-800',
                                retired: 'bg-n-100 text-n-600',
                                missing: 'bg-red-100 text-red-800',
                              }[d.status] || 'bg-n-100 text-n-600';
                              return (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${c}`}>
                                  {(d.status || '').replace(/_/g, ' ')}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'device-detail' && (
          <div>
            <button onClick={() => setView('devices')} className="text-n-600 hover:text-n-900 font-medium text-sm mb-4">← Back to Devices</button>

            {!selectedDevice ? (
              <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-12 text-center text-n-500">Loading device...</div>
            ) : (
              <>
                {/* Header */}
                <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8 mb-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="bg-n-200 w-14 h-14 rounded-full flex items-center justify-center shrink-0">
                        <Laptop className="w-7 h-7 text-n-600" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-n-900">{selectedDevice.hostname || selectedDevice.asset_tag || 'Device'}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          {(() => {
                            const c = {
                              in_service: 'bg-green-100 text-green-800',
                              spare: 'bg-blue-100 text-blue-800',
                              repair: 'bg-yellow-100 text-yellow-800',
                              retired: 'bg-n-100 text-n-600',
                              missing: 'bg-red-100 text-red-800',
                            }[selectedDevice.status] || 'bg-n-100 text-n-600';
                            return (
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${c}`}>
                                {(selectedDevice.status || '').replace(/_/g, ' ')}
                              </span>
                            );
                          })()}
                          <span className="text-n-500 text-sm">{(selectedDevice.device_class || '').replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                    </div>
                    {selectedDevice.hostname && (
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end flex-wrap">
                          <button
                            type="button"
                            onClick={() => launchShadow(selectedDevice)}
                            style={{ backgroundColor: PRIMARY_COLOR }}
                            className="px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition text-sm font-medium"
                          >
                            Shadow Session
                          </button>
                          <button
                            type="button"
                            onClick={() => launchRdp(selectedDevice)}
                            className="px-4 py-2.5 border border-n-300 text-n-700 rounded-lg hover:bg-n-100 transition text-sm font-medium"
                          >
                            Connect via RDP
                          </button>
                        </div>
                        <p className="text-xs text-n-500 mt-1.5 max-w-[19rem]">
                          Shadow joins the signed-in user's screen. RDP locks them out (session preserved).
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Identity */}
                <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8 mb-6">
                  <h3 className="text-lg font-bold text-n-900 mb-4">Identity</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Serial / Service Tag</div>
                      <div className="text-n-900 mt-0.5 break-all">{selectedDevice.serial || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Manufacturer</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.manufacturer || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Model</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.model || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">OS Build</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.os_build || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Intune Device ID</div>
                      <div className="text-n-900 mt-0.5 break-all">{selectedDevice.intune_device_id || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Last Check-in</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.last_checkin ? new Date(selectedDevice.last_checkin).toLocaleString() : '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Assignment & records */}
                <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8 mb-6">
                  <h3 className="text-lg font-bold text-n-900 mb-4">Assignment &amp; records</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Assigned To</div>
                      {editingAssigned ? (
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="text"
                            value={assignedInput}
                            onChange={(e) => setAssignedInput(e.target.value)}
                            placeholder="name@pvrhs.org"
                            className="flex-1 px-3 py-1.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent text-sm"
                          />
                          <button
                            onClick={saveAssigned}
                            disabled={savingAssigned}
                            style={{ backgroundColor: PRIMARY_COLOR }}
                            className="px-3 py-1.5 text-white rounded-lg hover:opacity-90 transition text-sm font-medium disabled:opacity-50"
                          >
                            {savingAssigned ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingAssigned(false)}
                            disabled={savingAssigned}
                            className="px-3 py-1.5 text-n-600 hover:bg-n-100 rounded-lg transition text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="mt-0.5 flex items-center gap-2">
                          <span className="text-n-900 break-all">{selectedDevice.assigned_user || '—'}</span>
                          <button
                            onClick={() => { setAssignedInput(selectedDevice.assigned_user || ''); setEditingAssigned(true); }}
                            className="text-pv-700 dark:text-pv-600 hover:bg-n-100 rounded px-2 py-0.5 text-xs font-medium"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Location</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.location_name || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Room</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.room || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Warranty Ends</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.warranty_end ? new Date(selectedDevice.warranty_end).toLocaleDateString() : '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Source</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.source || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-n-500 uppercase tracking-wider">Last Synced</div>
                      <div className="text-n-900 mt-0.5">{selectedDevice.last_synced_at ? new Date(selectedDevice.last_synced_at).toLocaleString() : '—'}</div>
                    </div>
                  </div>
                </div>

                {/* Ticket history */}
                <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
                  <h3 className="text-lg font-bold text-n-900 mb-4">Ticket history</h3>
                  {deviceTickets.length === 0 ? (
                    <p className="text-n-500">No tickets reference this device yet.</p>
                  ) : (
                    <div className="divide-y divide-n-200">
                      {deviceTickets.map(t => (
                        <div key={t.id} className="py-3 flex items-center justify-between gap-4">
                          <div className="text-n-900 font-medium">#{t.id} {t.title}</div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-n-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</span>
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-n-100 text-n-600">{t.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {view === 'users' && (
          <div className="bg-surface rounded-2xl shadow-sm border border-n-200 p-8">
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-n-900">User Management</h2>
                <p className="text-n-600 mt-1">Create and manage user accounts</p>
              </div>
              <button 
                onClick={openCreateUserModal}
                style={{backgroundColor: PRIMARY_COLOR}}
                className="px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add User
              </button>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-n-400" />
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                    />
                  </div>
                </div>
                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="tech">Tech Team</option>
                  <option value="tech-admin">Tech Admin</option>
                  <option value="facilities">Facilities</option>
                  <option value="facilities-admin">Facilities Admin</option>
                  <option value="custodian">Custodian</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
            </div>

            {/* Users Count */}
            <div className="mb-4 text-sm text-n-600">
              Showing {filteredUsers.length} of {users.length} users
            </div>

            {/* Users Table */}
            <div className="bg-surface rounded-xl shadow-sm border border-n-200 overflow-hidden">
              {loading ? (
                <div className="text-center py-12">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="bg-n-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-n-400" />
                  </div>
                  <p className="text-n-500 text-lg font-medium">No users found</p>
                  <p className="text-n-400 mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-n-50 border-b border-n-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-n-600 uppercase tracking-wider">Department</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-n-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-n-200">
                      {filteredUsers.map(u => (
                        <tr key={u.id} className="hover:bg-n-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="bg-n-200 w-10 h-10 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-n-600" />
                              </div>
                              <button
                                onClick={() => openUserDetail(u.id)}
                                className="font-medium text-n-900 hover:underline text-left"
                              >
                                {u.name}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-n-600">{u.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                              {getRoleDisplayName(u.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-n-600">
                            {u.department ? (u.department === 'technology' ? 'Technology' : 'Facilities') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditUserModal(u)}
                                className="px-3 py-1.5 text-pv-700 dark:text-pv-600 hover:bg-n-100 rounded-lg transition text-sm font-medium"
                              >
                                Edit
                              </button>
                              {u.id !== user?.id && (
                                <button
                                  onClick={() => setShowDeleteUserConfirm(u)}
                                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition text-sm font-medium"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Create/Edit Modal */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-n-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-n-900">
                    {editingUser ? 'Edit User' : 'Create New User'}
                  </h3>
                  <button onClick={closeUserModal} className="text-n-400 hover:text-n-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                    placeholder="jsmith@pvrhs.org"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">
                    Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                    placeholder={editingUser ? '••••••••' : 'Enter password'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Role *</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                  >
                    <option value="staff">Staff</option>
                    <option value="tech">Tech Team</option>
                    <option value="tech-admin">Tech Admin</option>
                    <option value="facilities">Facilities</option>
                    <option value="facilities-admin">Facilities Admin</option>
                    <option value="custodian">Custodian</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="text-xs text-n-500 mt-1">
                    {userForm.role === 'staff' && 'Can only see and manage their own tickets'}
                    {userForm.role === 'tech' && 'Full access to technology department tickets'}
                    {userForm.role === 'tech-admin' && 'Tech team lead with admin privileges'}
                    {userForm.role === 'facilities' && 'Full access to facilities department tickets'}
                    {userForm.role === 'facilities-admin' && 'Facilities team lead with admin privileges'}
                    {userForm.role === 'custodian' && 'Can submit facilities tickets, only sees own tickets'}
                    {userForm.role === 'admin' && 'Full system access including user management'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">New ticket alerts</label>
                  <select
                    value={userForm.notify_new_ticket_alerts}
                    onChange={(e) => setUserForm({...userForm, notify_new_ticket_alerts: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                  >
                    <option value="role">Follow the role rules in Settings</option>
                    <option value="always">Always, regardless of role</option>
                    <option value="never">Never</option>
                  </select>
                  <p className="text-xs text-n-500 mt-1">
                    Only affects alerts for newly created tickets. Replies and updates on their own tickets are sent regardless.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-n-700 mb-2">Department</label>
                  <select
                    value={userForm.department}
                    onChange={(e) => setUserForm({...userForm, department: e.target.value})}
                    className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="technology">Technology</option>
                    <option value="facilities">Facilities</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-n-200 flex justify-end gap-3">
                <button
                  onClick={closeUserModal}
                  className="px-4 py-2.5 bg-n-100 text-n-700 rounded-lg hover:bg-n-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={loading}
                  style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
                  className="px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium"
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteUserConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-n-900">Delete User</h3>
                  <p className="text-n-600">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-n-700 mb-6">
                Are you sure you want to delete <strong>{showDeleteUserConfirm.name}</strong>? 
                Any tickets they have submitted will be preserved but no longer associated with an active user.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteUserConfirm(null)}
                  className="px-4 py-2.5 bg-n-100 text-n-700 rounded-lg hover:bg-n-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteUserConfirm.id)}
                  disabled={loading}
                  className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                >
                  {loading ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ============ END USERS MANAGEMENT VIEW ============ */}

        {view === 'detail' && selectedTicket && (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <button onClick={() => setView('tickets')} className="text-n-600 hover:text-n-900 font-medium text-sm mb-4">← Back to Tickets</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-n-900 mb-3">{selectedTicket.title}</h2>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-md text-sm font-medium border ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority.toUpperCase()}</span>
                        <span className={`px-3 py-1.5 rounded-md text-sm font-medium border flex items-center gap-1.5 ${getStatusColor(selectedTicket.status)}`}>
                          {getStatusIcon(selectedTicket.status)}
                          {selectedTicket.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-n-200 pt-4">
                    <h3 className="text-sm font-semibold text-n-700 mb-3">Description</h3>
                    <p className="text-n-700 leading-relaxed whitespace-pre-wrap">{selectedTicket.description}</p>
					</div>
                  {isTechTeam && (
                    <div className="border-t border-n-200 pt-4 mt-4">
                      <h3 className="text-sm font-semibold text-n-700 mb-3">Related Device</h3>
                      {selectedTicket.asset_id && !showTicketDevicePicker ? (
                        <div className="flex items-center gap-3 flex-wrap">
                          <button
                            type="button"
                            onClick={() => openDevice(selectedTicket.asset_id)}
                            className="flex items-center gap-2 text-pv-700 hover:text-pv-800 dark:text-pv-600 dark:hover:text-pv-100 text-sm font-medium"
                          >
                            <Laptop className="w-4 h-4" />
                            {selectedTicket.device_hostname || selectedTicket.device_asset_tag || `Device #${selectedTicket.asset_id}`}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowTicketDevicePicker(true); if (devices.length === 0) fetchDevices(); }}
                            className="text-xs text-n-600 hover:bg-n-100 rounded px-2 py-0.5 font-medium"
                          >
                            Change
                          </button>
                          <button
                            type="button"
                            onClick={() => linkTicketDevice(null)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      ) : showTicketDevicePicker ? (
                        <div className="relative">
                          <input
                            type="text"
                            autoFocus
                            value={ticketDeviceSearch}
                            onChange={(e) => setTicketDeviceSearch(e.target.value)}
                            placeholder="Search by hostname, tag, or assigned user..."
                            className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent"
                          />
                          {ticketDeviceSearch && (
                            <div className="absolute z-10 w-full mt-1 bg-surface border border-n-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {devices
                                .filter(d => {
                                  const q = ticketDeviceSearch.toLowerCase();
                                  return [d.hostname, d.asset_tag, d.model, d.assigned_user]
                                    .some(v => (v || '').toLowerCase().includes(q));
                                })
                                .slice(0, 10)
                                .map(d => (
                                  <div
                                    key={d.id}
                                    onMouseDown={() => linkTicketDevice(d.id)}
                                    className="px-4 py-2 hover:bg-n-100 cursor-pointer"
                                  >
                                    <div className="font-medium text-n-900">{d.hostname || d.asset_tag || `Device #${d.id}`}</div>
                                    <div className="text-xs text-n-500">{[d.model, d.assigned_user].filter(Boolean).join(' · ') || '—'}</div>
                                  </div>
                                ))}
                              {devices.filter(d => {
                                const q = ticketDeviceSearch.toLowerCase();
                                return [d.hostname, d.asset_tag, d.model, d.assigned_user]
                                  .some(v => (v || '').toLowerCase().includes(q));
                              }).length === 0 && (
                                <div className="px-4 py-3 text-n-500 text-sm">No devices found</div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => { setShowTicketDevicePicker(false); setTicketDeviceSearch(''); }}
                            className="mt-2 text-xs text-n-600 hover:text-n-900 font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setShowTicketDevicePicker(true); if (devices.length === 0) fetchDevices(); }}
                          className="text-sm text-pv-700 hover:text-pv-800 dark:text-pv-600 dark:hover:text-pv-100 font-medium"
                        >
                          + Attach a device
                        </button>
                      )}
                    </div>
                  )}
                  {selectedTicket.resolution && (
                    <div className="border-t border-n-200 pt-4 mt-4">
                      <div className="p-4 rounded-lg border border-pv-600/30" style={{background: 'linear-gradient(135deg, rgba(45, 80, 22, 0.05) 0%, rgba(45, 80, 22, 0.12) 100%), rgb(var(--surface))'}}>
                        <h3 className="text-sm font-semibold text-n-900 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-pv-700 dark:text-pv-600" />
                          Resolution
                        </h3>
                        <p className="text-n-700 whitespace-pre-wrap">{selectedTicket.resolution}</p>
                      </div>
                    </div>
                  )}
                  {(() => {
  try {
    let attachments;
    
    if (Array.isArray(selectedTicket.attachments)) {
      attachments = selectedTicket.attachments;
    }
    else if (typeof selectedTicket.attachments === 'string' && selectedTicket.attachments.trim() !== '') {
      attachments = JSON.parse(selectedTicket.attachments);
    }
    else {
      return null;
    }
    
    if (attachments && attachments.length > 0) {
      return (
        <div className="border-t border-n-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-n-700 mb-3">Attachments</h3>
          <div className="space-y-2">
            {attachments.map((filename, index) => (
              <button
                key={index}
                type="button"
                onClick={() => openAttachment(filename)}
                className="flex items-center gap-2 text-pv-700 hover:text-pv-800 dark:text-pv-600 dark:hover:text-pv-100 text-sm"
              >
                <FileText className="w-4 h-4" />
                {filename.substring(filename.indexOf('-') + 1)}
              </button>
            ))}
          </div>
        </div>
      );
    }
  } catch (e) {
    console.warn('Error parsing ticket attachments:', e);
    return null;
  }
  return null;
})()}
                </div>
                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <h3 className="text-lg font-semibold text-n-900 mb-4">Activity & Comments</h3>
                  <div className="space-y-4 mb-6">
                    {comments.length === 0 ? (
                      <p className="text-n-500 text-center py-8">No comments yet</p>
                    ) : (
                      comments.map(comment => (
                        <div key={comment.id} className="bg-n-50 rounded-lg p-4 border border-n-200">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="bg-n-200 w-8 h-8 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-n-600" /></div>
                              <div>
                                <p className="font-medium text-n-900">{comment.author_name}</p>
                                <span className="text-xs text-n-500">{new Date(comment.created_at).toLocaleString()}</span>
                              </div>
                            </div>
                            
                            {/* Delete button - only show if user can delete */}
                            {(user?.id === comment.author_id || 
                              user?.role === 'admin' || 
                              user?.role === 'tech-admin' || 
                              user?.role === 'facilities-admin') && (
                              <button
                                onClick={() => deleteComment(comment.id)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="Delete comment"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-n-700 ml-10">{comment.text}</p>
						 {(() => {
  try {
    let attachments;
    
    if (Array.isArray(comment.attachments)) {
      attachments = comment.attachments;
    }
    else if (typeof comment.attachments === 'string' && comment.attachments.trim() !== '') {
      attachments = JSON.parse(comment.attachments);
    }
    else {
      return null;
    }
    
    if (attachments && attachments.length > 0) {
      return (
        <div className="ml-10 mt-2 space-y-1">
          {attachments.map((filename, index) => (
            <button
              key={index}
              type="button"
              onClick={() => openAttachment(filename)}
              className="flex items-center gap-2 text-pv-700 hover:text-pv-800 dark:text-pv-600 dark:hover:text-pv-100 text-xs"
            >
              <FileText className="w-3 h-3" />
              {filename.substring(filename.indexOf('-') + 1)}
            </button>
          ))}
        </div>
      );
    }
  } catch (e) {
    console.warn('Error parsing comment attachments:', e);
    return null;
  }
  return null;
})()}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="border-t border-n-200 pt-4">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full px-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent mb-2"
                      rows="3"
                    />
                    <div className="flex items-center gap-3">
                      <input 
                        type="file" 
                        multiple 
                        id="comment-attachments"
                        onChange={(e) => setCommentAttachments(Array.from(e.target.files))}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        className="hidden"
                      />
                      <label 
                        htmlFor="comment-attachments"
                        className="px-4 py-2 bg-n-100 text-n-700 rounded-lg hover:bg-n-200 transition cursor-pointer text-sm font-medium"
                      >
                        📎 Attach Files
                      </label>
                      {commentAttachments.length > 0 && (
                        <span className="text-sm text-n-600">
                          {commentAttachments.length} file(s)
                        </span>
                      )}
                      <button 
                        onClick={handleAddComment}
                        style={{backgroundColor: PRIMARY_COLOR}} 
                        className="ml-auto px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 transition flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Add Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                  <h3 className="text-sm font-semibold text-n-700 mb-4">Ticket Details</h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-n-500 mb-1">Department</p>
                      <p className="font-medium text-n-900">{selectedTicket.department === 'technology' ? 'Technology' : 'Facilities'}</p>
                    </div>
                    <div>
                      <p className="text-n-500 mb-1">Location</p>
                      <p className="font-medium text-n-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-n-400" />{selectedTicket.location}</p>
                    </div>
                    <div>
                      <p className="text-n-500 mb-1">Submitted by</p>
                      <p className="font-medium text-n-900 flex items-center gap-2"><User className="w-4 h-4 text-n-400" />
                        <UserLink
                          id={selectedTicket.submitted_by}
                          name={selectedTicket.submitted_by_name}
                          viewer={user}
                          onOpen={openUserDetail}
                        />
                      </p>
                    </div>
                    <div>
                      <p className="text-n-500 mb-1">Created</p>
                      <p className="font-medium text-n-900 flex items-center gap-2"><Calendar className="w-4 h-4 text-n-400" />{new Date(selectedTicket.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
				{/* Device Information - Only show to tech team for tech tickets */}
                {selectedTicket.department === 'technology' && selectedTicket.device_info && 
                 (user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || user?.department === 'technology') && (
                  <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6 mt-6">
                    <h3 className="text-sm font-semibold text-n-700 mb-4">Device Information</h3>
                    <div className="space-y-3 text-sm">
                      {(() => {
                        try {
                          const deviceInfo = JSON.parse(selectedTicket.device_info);
                          return (
                            <>
                              {deviceInfo.hostname && (
                                <div>
                                  <p className="text-n-500 mb-1">Computer Name</p>
                                  <p className="font-medium text-n-900">{deviceInfo.hostname}</p>
                                </div>
                              )}
                              {deviceInfo.ip_address && (
                                <div>
                                  <p className="text-n-500 mb-1">IP Address</p>
                                  <p className="font-medium text-n-900">{deviceInfo.ip_address}</p>
                                </div>
                              )}
                              {deviceInfo.os && (
                                <div>
                                  <p className="text-n-500 mb-1">Operating System</p>
                                  <p className="font-medium text-n-900">{deviceInfo.os}</p>
                                </div>
                              )}
                              {deviceInfo.browser && (
                                <div>
                                  <p className="text-n-500 mb-1">Browser</p>
                                  <p className="font-medium text-n-900">{deviceInfo.browser}</p>
                                </div>
                              )}
                              {deviceInfo.screen_resolution && (
                                <div>
                                  <p className="text-n-500 mb-1">Screen Resolution</p>
                                  <p className="font-medium text-n-900">{deviceInfo.screen_resolution}</p>
                                </div>
                              )}
                            </>
                          );
                        } catch (e) {
                          return <p className="text-n-500">Device information unavailable</p>;
                        }
                      })()}
                    </div>
                  </div>
                )}
                {(user?.role === 'admin' || 
                  user?.role === 'tech' || 
                  user?.role === 'tech-admin' || 
                  user?.role === 'facilities' || 
                  user?.role === 'facilities-admin' ||
                  user?.department === 'technology' || 
                  user?.department === 'facilities') && user?.role !== 'custodian' && (
                  <div className="bg-surface rounded-xl shadow-sm border border-n-200 p-6">
                    <h3 className="text-sm font-semibold text-n-700 mb-4">Manage Ticket</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-n-700 mb-2">Status</label>
                        <select value={ticketDraft.status} onChange={(e) => setTicketDraft({ ...ticketDraft, status: e.target.value })} className="w-full px-3 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                      {(ticketDraft.status === 'resolved') && (
                        <div>
                          <label className="block text-sm font-medium text-n-700 mb-2">Resolution <span className="text-red-500">*</span></label>
                          <textarea 
                            value={ticketDraft.resolution || ''} 
                            onChange={(e) => setTicketDraft({ ...ticketDraft, resolution: e.target.value })}
                            placeholder="Describe how the issue was resolved..."
                            className="w-full px-3 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600"
                            rows={3}
                          />
                        </div>
                      )}
					  <div>
  <label className="block text-sm font-medium text-n-700 mb-2">Department</label>
  <select value={ticketDraft.department} onChange={(e) => setTicketDraft({ ...ticketDraft, department: e.target.value })} className="w-full px-3 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
    <option value="technology">Technology</option>
    <option value="facilities">Facilities</option>
  </select>
</div>
                      <div>
                        <label className="block text-sm font-medium text-n-700 mb-2">Priority</label>
                        <select value={ticketDraft.priority} onChange={(e) => setTicketDraft({ ...ticketDraft, priority: e.target.value })} className="w-full px-3 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-n-700 mb-2">Assign To</label>
                        <select value={ticketDraft.assigned_to} onChange={(e) => setTicketDraft({ ...ticketDraft, assigned_to: e.target.value })} className="w-full px-3 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600">
                          <option value="">Unassigned</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>{member.name}</option>
                          ))}
                        </select>
                      </div>
					  <div className="pt-4">
  <button 
    onClick={saveTicketChanges}
    disabled={loading}
    style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
    className="w-full px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium disabled:opacity-50"
  >
    {loading ? 'Saving...' : 'Save Changes'}
  </button>
</div>
                      <div className="pt-4 mt-4 border-t border-n-200 space-y-3">
                        <button 
                          onClick={handleOpenMergeModal}
                          className="w-full px-4 py-2 bg-pv-800 text-white rounded-lg hover:bg-pv-700 transition font-medium"
                        >
                          Merge Tickets
                        </button>
                        <button 
                          onClick={() => setShowDeleteConfirm(true)}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                        >
                          Delete Ticket
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-xl p-6 max-w-md w-full mx-4">
                      <h3 className="text-xl font-bold text-n-900 mb-2">Delete Ticket?</h3>
                      <p className="text-n-600 mb-6">Are you sure you want to delete this ticket? It will be moved to the deleted tickets area and can be restored later.</p>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => deleteTicket(selectedTicket.id)}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                        >
                          Yes, Delete
                        </button>
                        <button 
                          onClick={() => setShowDeleteConfirm(false)}
                          className="flex-1 px-4 py-2 bg-n-200 text-n-700 rounded-lg hover:bg-n-300 transition font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <TicketHistory
              ticketId={selectedTicket.id}
              token={token}
              viewer={user}
              onOpenUser={openUserDetail}
            />
          </div>
        )}

        {view === 'user-detail' && selectedUserId && (
          <UserDetail
            userId={selectedUserId}
            token={token}
            viewer={user}
            onBack={() => { setSelectedUserId(null); setView('users'); }}
            onOpenTicket={openTicketById}
            onOpenUser={openUserDetail}
          />
        )}
      </div>
      </div>

      {/* Modals live outside the md:pl-64 column on purpose: they are fixed
          overlays and should cover the sidebar, not sit beside it. */}

      {/* Attachment preview */}
      {attachmentPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeAttachmentPreview}>
          <div className="bg-surface rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-n-200 flex-shrink-0">
              <h3 className="text-sm font-medium text-n-900 truncate">{attachmentPreview.name}</h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => saveBlobAs(attachmentPreview.url, attachmentPreview.name)}
                  className="p-2 text-n-600 hover:text-n-900 transition"
                  title="Download"
                  aria-label="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button onClick={closeAttachmentPreview} className="p-2 text-n-600 hover:text-n-900 transition" title="Close" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-n-100 flex items-center justify-center p-4 min-h-0">
              {attachmentPreview.kind === 'pdf' ? (
                <iframe title={attachmentPreview.name} src={attachmentPreview.url} className="w-full h-[75vh] rounded bg-surface" />
              ) : (
                <img src={attachmentPreview.url} alt={attachmentPreview.name} className="max-w-full max-h-[75vh] object-contain" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge Tickets Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseMergeModal}>
          <div className="bg-surface rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-n-900">Merge Tickets into Ticket #{selectedTicket?.id}</h3>
                <p className="text-sm text-n-600 mt-1">Select tickets to merge. All comments and attachments will be moved to this ticket.</p>
              </div>
              <button onClick={handleCloseMergeModal} className="p-1 hover:bg-n-100 rounded">
                <X className="w-5 h-5 text-n-500" />
              </button>
            </div>
            
            {/* Search bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-n-400" />
                <input 
                  type="text" 
                  value={mergeSearchQuery} 
                  onChange={(e) => setMergeSearchQuery(e.target.value)} 
                  placeholder="Search tickets by title, location, or submitter..." 
                  className="w-full pl-10 pr-4 py-2.5 border border-n-300 rounded-lg focus:ring-2 focus:ring-pv-600 focus:border-transparent" 
                />
              </div>
            </div>
            
            {/* Tickets list */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {mergeLoading ? (
                <div className="text-center py-12 text-n-500">Loading tickets...</div>
              ) : mergeableTickets.filter(ticket => {
                if (!mergeSearchQuery) return true;
                const query = mergeSearchQuery.toLowerCase();
                return ticket.title.toLowerCase().includes(query) ||
                       ticket.location.toLowerCase().includes(query) ||
                       (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(query));
              }).length === 0 ? (
                <div className="text-center py-12 text-n-500">
                  {mergeSearchQuery ? 'No tickets match your search' : 'No mergeable tickets found'}
                </div>
              ) : (
                <div className="divide-y">
                  {mergeableTickets.filter(ticket => {
                    if (!mergeSearchQuery) return true;
                    const query = mergeSearchQuery.toLowerCase();
                    return ticket.title.toLowerCase().includes(query) ||
                           ticket.location.toLowerCase().includes(query) ||
                           (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(query));
                  }).map(ticket => (
                    <label 
                      key={ticket.id} 
                      className="flex items-start gap-4 p-4 hover:bg-n-50 cursor-pointer transition"
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedMergeTickets.includes(ticket.id)}
                        onChange={() => toggleMergeTicketSelection(ticket.id)}
                        className="mt-1.5 w-4 h-4 text-pv-800 rounded focus:ring-2 focus:ring-pv-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-n-900">#{ticket.id}</span>
                          <h4 className="font-semibold text-n-900 truncate">{ticket.title}</h4>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-n-600">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority.toUpperCase()}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {ticket.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.submitted_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.comment_count || 0} comments
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer with action buttons */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <div className="text-sm text-n-600">
                {selectedMergeTickets.length > 0 ? (
                  <span className="font-medium">{selectedMergeTickets.length} ticket(s) selected</span>
                ) : (
                  <span>Select tickets to merge</span>
                )}
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={handleCloseMergeModal}
                  className="px-4 py-2 bg-n-200 text-n-700 rounded-lg hover:bg-n-300 transition font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleMergeTickets}
                  disabled={selectedMergeTickets.length === 0 || mergeLoading}
                  className="px-4 py-2 bg-pv-800 text-white rounded-lg hover:bg-pv-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mergeLoading ? 'Merging...' : `Merge ${selectedMergeTickets.length} Ticket(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Comments Popup Modal */}
      {showCommentsForTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseComments}>
          <div className="bg-surface rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-n-900">Comments for: {showCommentsForTicket.title}</h3>
              <button onClick={handleCloseComments} className="p-1 hover:bg-n-100 rounded">
                <X className="w-5 h-5 text-n-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3">
              {cardCommentsLoading ? (
                <p className="text-n-500 text-center py-8">Loading comments...</p>
              ) : cardComments.length === 0 ? (
                <p className="text-n-500 text-center py-8">No comments yet</p>
              ) : (
                cardComments.map(comment => (
                  <div key={comment.id} className="bg-n-50 rounded-lg p-3 border border-n-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-n-200 w-6 h-6 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-n-600" />
                      </div>
                      <span className="font-medium text-n-900 text-sm">{comment.author_name}</span>
                      <span className="text-xs text-n-500">{new Date(comment.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-n-700 text-sm ml-8">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button 
                onClick={() => { handleCloseComments(); openTicketDetail(showCommentsForTicket); }}
                style={{backgroundColor: PRIMARY_COLOR}}
                className="w-full px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium"
              >
                View Full Ticket Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
